<svelte:options customElement="smr-chat-widget" />

<script lang="ts">
	import {
		PUBLIC_CHAT_EMBED_INSTANCE_NAME,
		PUBLIC_CHAT_EMBED_TENANT_NAME
	} from '$env/static/public'
	import { ChatWindow } from '@repo/commons/components'
	import type { ChatMessage, ChatStatus, MessageActionType } from '@repo/commons/models'
	import { copyToClipboard, generateId, joinRegisterInputMessages } from '@repo/commons/utils'
	import {
		InteractionResultInteractionResultTypeEnum,
		type InteractionResult
	} from '@semanticedge/chatbridge-noheaders-client/src'
	import { setupI18n, t } from '../lib/i18n'
	import {
		getInitialPrompt,
		initChatBridgeSession,
		registerInput,
		setChatServerUrl
	} from './../lib/api'
	import './../lib/styles/app.css'

	interface Prop {
		chatserverurl: string
		headercolor: string
		triggercolor?: string
		buttoncolor?: string
		instancename?: string
		tenantname?: string
	}

	setupI18n()

	let { chatserverurl, headercolor, triggercolor, buttoncolor, instancename, tenantname }: Prop =
		$props()

	let chatWindowRef: ChatWindow | null = $state(null)
	let sessionId: string | null = $state(null)
	let chatMessages: ChatMessage[] = $state([])
	let isChatVisible = $state(false)
	let chatStatus: ChatStatus = $state('loading')
	let isConfirmNewChatOpen = $state(false)

	const MESSAGE_SETTLE_MS = 500

	function handleScroll() {
		setTimeout(() => chatWindowRef?.scrollToBottom(), 0)
		setTimeout(() => chatWindowRef?.scrollToBottom(), MESSAGE_SETTLE_MS)
	}

	function clearMessages() {
		chatMessages = []
	}

	async function initNewSessionAndGetPrompt() {
		const instanceName = instancename || PUBLIC_CHAT_EMBED_INSTANCE_NAME
		const tenantName = tenantname || PUBLIC_CHAT_EMBED_TENANT_NAME
		console.log(`Initializing session with instance: ${instanceName} and tenant: ${tenantName}`)
		chatMessages = []
		sessionId = await initChatBridgeSession({ instanceName, tenantName })
		const initialPromptResponse: InteractionResult = await getInitialPrompt({ sessionId })
		addAssistantMessage(initialPromptResponse)
		return sessionId
	}

	function addAssistantMessage(response: InteractionResult) {
		const utterance = joinRegisterInputMessages(response.messages)
		const isHangup =
			response.interactionResultType === InteractionResultInteractionResultTypeEnum.SystemHangup

		if (!isHangup || utterance) {
			chatMessages = [
				...chatMessages,
				{
					utterance,
					role: 'ASSISTANT',
					interactionId: response.interactionId ?? generateId(),
					responded: false,
					isForm: false
				}
			]
		}

		if (isHangup) {
			chatMessages = [
				...chatMessages,
				{ utterance: '', role: 'SYSTEM', interactionId: generateId(), isConversationEnd: true }
			]
		}
	}

	function addUserMessage(message: string) {
		chatMessages = [
			...chatMessages,
			{
				utterance: message,
				role: 'USER',
				responded: false,
				isForm: false,
				interactionId: generateId()
			}
		]
	}

	function onCloseCallback(step: 'CLOSE_WINDOW' | 'END_CHAT' | 'SHOW_FEEDBACK') {
		if (step === 'CLOSE_WINDOW') {
			isChatVisible = false
		} else if (step === 'END_CHAT') {
			isChatVisible = false
			chatStatus = 'loading'
			sessionId = null
			clearMessages()
		} else {
			chatStatus = 'feedback'
		}
	}

	async function openChat() {
		try {
			isChatVisible = true
			if (!sessionId) {
				chatStatus = 'loading'
				sessionId = await initNewSessionAndGetPrompt()
			}
			handleScroll()
			chatStatus = 'opened'
		} catch (err) {
			console.error(err)
			chatStatus = 'feedback'
		}
	}

	function handleActionCallback(action: MessageActionType, interactionId: string) {
		if (action === 'COPY') {
			const item = chatMessages.find((m) => m.interactionId === interactionId)
			if (item) {
				copyToClipboard(item.utterance)
			}
		}
	}

	async function sendMessage(message: string) {
		addUserMessage(message)
		handleScroll()

		try {
			const response: InteractionResult = await registerInput({
				sessionId: sessionId ?? '',
				userInput: message
			})
			addAssistantMessage(response)
		} catch (err) {
			console.error('Failed to register input:', err)
		}

		handleScroll()
	}

	async function startNewChatWithNewSession() {
		try {
			chatStatus = 'loading'
			sessionId = await initNewSessionAndGetPrompt()
			handleScroll()
			chatStatus = 'opened'
			isConfirmNewChatOpen = false
		} catch (error) {
			console.error(error)
			chatStatus = 'feedback'
		}
	}

	function setConfirmNewChatOpen(value: boolean) {
		isConfirmNewChatOpen = value
	}

	$effect(() => {
		if (chatserverurl) {
			setChatServerUrl(chatserverurl)
		}
	})
</script>

<ChatWindow
	bind:this={chatWindowRef}
	headerColor={headercolor}
	triggerColor={triggercolor}
	buttonColor={buttoncolor}
	{chatMessages}
	{isChatVisible}
	{isConfirmNewChatOpen}
	{chatStatus}
	chatTitle={$t('CustomerSupport.Label')}
	visibleLangAction={false}
	allowMessageEditing={false}
	showFeedbackButtons={false}
	showAttachButton={false}
	onSendMessage={sendMessage}
	onSetConfirmNewChatOpen={setConfirmNewChatOpen}
	onStartNewChatWithNewSession={startNewChatWithNewSession}
	onHandleActionCallback={handleActionCallback}
	onOpenChat={openChat}
	{onCloseCallback}
/>
